import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Save, UserPlus } from 'lucide-react';

import PageHeader from '../components/PageHeader.jsx';
import SaveBar, { SaveButton } from '../components/SaveBar.jsx';
import FormField, { TextInput } from '../components/FormField.jsx';
import { createCustomer } from '../api/customers';

/**
 * New Customer flow.
 *
 * Field rep meets a new customer in person → adds them on the spot →
 * jumps straight back into NewQuote / NewSale to quote them. The
 * inline-add path uses location.state.returnTo to bring the rep back
 * to the originating form with the new customer pre-selected via
 * `newCustomerId` on the return state.
 *
 * Direct entry (from /customers list "+ New" FAB) has no returnTo, so
 * we land the rep on the new customer's detail page after save.
 *
 * Field set mirrors the desktop CustomerForm exactly. Only `name`
 * (business name) is required — everything else is optional so reps
 * capture what they know in the moment without the form blocking on
 * fields they don't have yet. Admins can fill in the rest later on
 * desktop.
 */
export default function NewCustomer() {
  const navigate = useNavigate();
  const location = useLocation();
  // Where to navigate after a successful save. Defaulting to the new
  // customer's detail page; inline-add callers (NewQuote / NewSale)
  // pass `state.returnTo` so we send the rep back to the form they
  // came from with the new customer's id in state.
  const returnTo = location.state?.returnTo;

  const [name, setName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [tinNumber, setTinNumber] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Synchronous double-tap guard — same pattern as the rest of the
  // form pages. Closes the gap between tap and the `submitting` state
  // update where a second tap could fire.
  const submitInFlight = useRef(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitInFlight.current) return;
    if (!name.trim()) {
      setError('Business name is required.');
      return;
    }

    submitInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      // Build the payload — send only fields the rep filled in (null
      // for empty optional fields keeps the backend's interpretation
      // unambiguous: "not provided" rather than "explicitly empty").
      const payload = {
        name: name.trim(),
        contact_name:  contactName.trim() || null,
        email:         email.trim() || null,
        phone:         phone.trim() || null,
        street_number: streetNumber.trim() || null,
        street:        street.trim() || null,
        city:          city.trim() || null,
        vat_number:    vatNumber.trim() || null,
        tin_number:    tinNumber.trim() || null,
        notes:         notes.trim() || null,
        credit_limit:  creditLimit === '' ? null : Number(creditLimit),
      };
      const customer = await createCustomer(payload);

      if (returnTo) {
        // Inline-add path — return the rep to the originating form
        // with the new customer's id so it can pre-select them.
        navigate(returnTo, {
          replace: true,
          state: {
            newCustomerId: customer.id,
            flash: `Added ${customer.name}`,
          },
        });
      } else {
        // Direct path — land on the customer's detail page.
        navigate(`/customers/${customer.id}`, {
          replace: true,
          state: { flash: `${customer.name} added` },
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to add customer');
    } finally {
      setSubmitting(false);
      submitInFlight.current = false;
    }
  }

  return (
    <main className="min-h-full px-4 pb-28">
      <PageHeader
        title="New Customer"
        subtitle={returnTo ? 'Add and continue' : 'Add a customer'}
        icon={UserPlus}
      />

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Required field — business / company name. The only enforced
            input. Everything else is optional so reps can capture in
            the moment without being blocked. */}
        <FormField label="Business name *">
          <TextInput
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. ACME Solar Installations"
          />
        </FormField>

        <FormField label="Contact person (optional)">
          <TextInput
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="John Smith"
          />
        </FormField>

        {/* Contact pair — email + phone side by side on wider mobile,
            stacked on narrow viewports via the natural FormField
            spacing. */}
        <FormField label="Phone (optional)">
          <TextInput
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+263 77 123 4567"
          />
        </FormField>

        <FormField label="Email (optional)">
          <TextInput
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="contact@example.co.zw"
          />
        </FormField>

        {/* Address — street_number + street + city. No.+ street on one
            row to keep them visually paired (mirrors how a Zimbabwean
            postal address is conventionally written). */}
        <div>
          <p className="text-small font-medium text-gray-700 mb-1.5">Address (optional)</p>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <FormField label="No.">
              <TextInput
                type="text"
                value={streetNumber}
                onChange={(e) => setStreetNumber(e.target.value)}
                placeholder="480"
              />
            </FormField>
            <div className="col-span-2">
              <FormField label="Street">
                <TextInput
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="Pomona Drive"
                />
              </FormField>
            </div>
          </div>
          <FormField label="City">
            <TextInput
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Harare"
            />
          </FormField>
        </div>

        {/* Tax IDs — VAT + TIN. Both important for fiscalisation but
            often not known until the rep follows up. */}
        <div className="grid grid-cols-2 gap-2">
          <FormField label="VAT number (optional)">
            <TextInput
              type="text"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
              placeholder="VAT123456"
            />
          </FormField>
          <FormField label="TIN number (optional)">
            <TextInput
              type="text"
              value={tinNumber}
              onChange={(e) => setTinNumber(e.target.value)}
              placeholder="TIN123456"
            />
          </FormField>
        </div>

        {/* Credit limit — null = no limit. Most field-rep adds will
            leave this blank; admins set it later from desktop. */}
        <FormField
          label="Credit limit USD (optional)"
          hint="Leave blank for no limit"
        >
          <TextInput
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={creditLimit}
            onChange={(e) => setCreditLimit(e.target.value)}
            placeholder=""
          />
        </FormField>

        <FormField label="Notes (optional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="e.g. Met at solar expo · interested in 5kW system"
            className="w-full px-3 py-3 border border-gray-300 rounded-md text-body focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </FormField>

        {error && <p role="alert" className="text-small text-danger">{error}</p>}

        <SaveBar>
          <SaveButton type="submit" icon={Save} loading={submitting} disabled={submitting}>
            {submitting ? 'Saving…' : returnTo ? 'Save and continue' : 'Save customer'}
          </SaveButton>
        </SaveBar>
      </form>
    </main>
  );
}
